import { round } from "./rng";

const cubicPoint = (p0, p1, p2, p3, t) => {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;
  return [
    a * p0[0] + b * p1[0] + c * p2[0] + d * p3[0],
    a * p0[1] + b * p1[1] + c * p2[1] + d * p3[1],
  ];
};

/**
 * Mughal cusped (scalloped) arch, as seen framing the reference card.
 * The arch sits in the box (x, y, w, h): the apex touches y, the curve springs
 * from the sides at y + spring, and the legs run down to y + h.
 * Each side is a row of outward-bulging lobes; the last lobe on each side is an
 * ogee that rises to a pointed apex.
 */
export const cuspedArch = ({
  x = 0,
  y = 0,
  w,
  h,
  spring,
  lobes = 6,
  bulge = 0.62,
  legs = true,
}) => {
  const cx = x + w / 2;
  const curve = [
    [x, y + spring],
    [x, y + spring * 0.4],
    [x + w * 0.24, y + spring * 0.08],
    [cx, y],
  ];
  // Lobes stop short of the apex, the ogee covers the remaining stretch.
  const left = Array.from({ length: lobes + 1 }, (_, i) =>
    cubicPoint(...curve, (i / lobes) * 0.86),
  );
  const mirror = ([px, py]) => [2 * cx - px, py];
  const right = left.map(mirror).reverse();

  const arc = (to, from) => {
    const r = Math.hypot(to[0] - from[0], to[1] - from[1]) * bulge;
    return ` A${round(r)} ${round(r)} 0 0 1 ${round(to[0])} ${round(to[1])}`;
  };

  let d = legs
    ? `M${round(x)} ${round(y + h)} L${round(left[0][0])} ${round(left[0][1])}`
    : `M${round(left[0][0])} ${round(left[0][1])}`;

  for (let i = 1; i < left.length; i++) d += arc(left[i], left[i - 1]);

  const s = left[left.length - 1];
  const rise = s[1] - y;
  d += ` C${round(s[0] + (cx - s[0]) * 0.05)} ${round(s[1] - rise * 0.95)} ${round(cx - (cx - s[0]) * 0.12)} ${round(y + rise * 0.5)} ${round(cx)} ${round(y)}`;
  const e = right[0];
  d += ` C${round(cx + (e[0] - cx) * 0.12)} ${round(y + rise * 0.5)} ${round(e[0] - (e[0] - cx) * 0.05)} ${round(e[1] - rise * 0.95)} ${round(e[0])} ${round(e[1])}`;

  for (let i = 1; i < right.length; i++) d += arc(right[i], right[i - 1]);

  if (legs) d += ` L${round(x + w)} ${round(y + h)}`;

  // Inner cusp points (where two lobes meet), with the direction pointing into the arch.
  const focus = [cx, y + spring * 0.75];
  const cusps = [...left.slice(1), ...right.slice(0, -1)].map(([px, py]) => {
    const angle = Math.atan2(focus[1] - py, focus[0] - px);
    return { x: px, y: py, angle: (angle * 180) / Math.PI };
  });

  return { d, cusps, apex: [cx, y] };
};

/** Smooth pointed arch outline (no cusps), closed along the bottom. */
export const pointedArch = ({ x = 0, y = 0, w, h, spring }) => {
  const cx = x + w / 2;
  return [
    `M${round(x)} ${round(y + h)}`,
    `L${round(x)} ${round(y + spring)}`,
    `C${round(x)} ${round(y + spring * 0.35)} ${round(x + w * 0.3)} ${round(y + spring * 0.1)} ${round(cx)} ${round(y)}`,
    `C${round(x + w * 0.7)} ${round(y + spring * 0.1)} ${round(x + w)} ${round(y + spring * 0.35)} ${round(x + w)} ${round(y + spring)}`,
    `L${round(x + w)} ${round(y + h)} Z`,
  ].join(" ");
};
